import React, { useState, useEffect, useRef } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Loader2, Package, Truck, CheckCircle2, XCircle, Printer, X, Download, 
  CreditCard, Banknote, AlertCircle, Calendar, FileText, ShieldAlert,
  Sparkles, AlertTriangle, CheckCircle, Info, MapPin, ExternalLink, Zap, XOctagon, Search
} from 'lucide-react';
import api from '../../utils/api';
import { formatDateTimeIST, formatDateIST, formatTimeIST } from '../../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get payment status display info
const getPaymentInfo = (order) => {
  const { payment_method, payment_status } = order;
  
  if (payment_method === 'cod') {
    return {
      label: 'COD',
      color: 'bg-amber-100 text-amber-800 border-amber-300',
      icon: <Banknote className="w-3 h-3" />,
      slipText: 'Cash on Delivery',
      isPrepaid: false,
    };
  }
  
  // Online payment (Razorpay)
  if (payment_status === 'paid') {
    return {
      label: 'Paid',
      color: 'bg-green-100 text-green-800 border-green-300',
      icon: <CreditCard className="w-3 h-3" />,
      slipText: 'Prepaid (Razorpay)',
      isPrepaid: true,
    };
  }
  
  if (payment_status === 'failed') {
    return {
      label: 'Failed',
      color: 'bg-red-100 text-red-800 border-red-300',
      icon: <AlertCircle className="w-3 h-3" />,
      slipText: 'Payment Failed',
      isPrepaid: false,
    };
  }
  
  // created / pending
  return {
    label: 'Pending',
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    icon: <CreditCard className="w-3 h-3" />,
    slipText: 'Payment Pending',
    isPrepaid: false,
  };
};

// Professional Dispatch Slip Component (A5 size - 148mm x 210mm)
// Includes Shadowfax AWB barcode when order is shipped via Shadowfax
const DispatchSlip = React.forwardRef(({ order }, ref) => {
  const barcodeRef = useRef(null);
  
  const hasShadowfax = !!(order?.shadowfax_awb);
  const awb = order?.shadowfax_awb || order?.tracking_number || '';

  // Generate barcode after render
  useEffect(() => {
    if (hasShadowfax && barcodeRef.current && awb) {
      import('jsbarcode').then((JsBarcode) => {
        try {
          JsBarcode.default(barcodeRef.current, awb, {
            format: 'CODE128',
            width: 1.5,
            height: 40,
            displayValue: true,
            fontSize: 12,
            font: 'monospace',
            margin: 4,
          });
        } catch (e) { /* barcode generation failed silently */ }
      });
    }
  }, [hasShadowfax, awb]);

  if (!order) return null;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const orderId = order.id?.slice(0, 8).toUpperCase() || 'N/A';
  
  // Get actual payment info
  const paymentInfo = getPaymentInfo(order);
  const isPaid = paymentInfo.isPrepaid;

  return (
    <div 
      ref={ref}
      id="dispatch-slip-content"
      style={{ 
        width: '148mm', 
        minHeight: '210mm',
        padding: '8mm',
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '11px',
        lineHeight: '1.4',
        color: '#1a1a1a',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
      }}
    >
      {/* Shadowfax AWB Barcode Section - TOP of slip when SFX order */}
      {hasShadowfax && (
        <div style={{
          border: '2px solid #4338ca',
          borderRadius: '8px',
          padding: '10px',
          marginBottom: '12px',
          backgroundColor: '#eef2ff',
          textAlign: 'center',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#4338ca' }}>
              SHADOWFAX
            </div>
            <div style={{
              fontSize: '11px', fontWeight: 'bold', color: '#fff',
              backgroundColor: isPaid ? '#16a34a' : '#ea580c',
              padding: '2px 10px', borderRadius: '12px',
            }}>
              {isPaid ? 'PREPAID' : 'COD ₹' + (order.total_amount?.toFixed(0) || '0')}
            </div>
          </div>
          <svg ref={barcodeRef} style={{ width: '100%', maxHeight: '55px' }}></svg>
          <div style={{ fontSize: '10px', color: '#4338ca', marginTop: '2px', fontWeight: '600' }}>
            AWB: {awb}
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        borderBottom: '3px solid #f97316',
        paddingBottom: '10px',
        marginBottom: '10px',
      }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f97316', letterSpacing: '-0.5px' }}>
            Tiffin & Bag
          </div>
          <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
            Premium Kids' Lunchboxes & School Bags
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1a1a1a' }}>
            DISPATCH SLIP
          </div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
            #{orderId}
          </div>
        </div>
      </div>

      {/* Order Info Bar */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        backgroundColor: '#f8f9fa',
        padding: '8px 10px',
        borderRadius: '6px',
        marginBottom: '10px',
      }}>
        <div>
          <div style={{ fontSize: '8px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Order Date</div>
          <div style={{ fontSize: '11px', fontWeight: '600', marginTop: '2px' }}>{formatDate(order.created_at)}</div>
          <div style={{ fontSize: '9px', color: '#666' }}>{formatTime(order.created_at)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '8px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Order ID</div>
          <div style={{ fontSize: '13px', fontWeight: 'bold', fontFamily: 'monospace', marginTop: '2px' }}>#{orderId}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '8px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment</div>
          <div style={{ 
            fontSize: '11px', 
            fontWeight: 'bold', 
            marginTop: '2px',
            color: isPaid ? '#16a34a' : '#ea580c',
            padding: '2px 8px',
            backgroundColor: isPaid ? '#dcfce7' : '#fff7ed',
            borderRadius: '4px',
            display: 'inline-block',
          }}>
            {paymentInfo.slipText}
          </div>
        </div>
      </div>

      {/* Shipping Address - Prominent */}
      <div style={{ 
        border: '2px dashed #d1d5db',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '10px',
        backgroundColor: '#fafafa',
      }}>
        <div style={{ 
          fontSize: '9px', color: '#666', textTransform: 'uppercase', 
          letterSpacing: '1px', marginBottom: '6px', fontWeight: '600',
        }}>
          Ship To:
        </div>
        <div style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '4px' }}>
          {order.delivery_address?.name || 'N/A'}
        </div>
        <div style={{ fontSize: '11px', lineHeight: '1.5' }}>
          {order.delivery_address?.line1 || ''}
          {order.delivery_address?.line2 && <><br/>{order.delivery_address.line2}</>}
          {order.delivery_address?.landmark && <><br/>Landmark: {order.delivery_address.landmark}</>}
          <br/>
          {order.delivery_address?.city}, {order.delivery_address?.state}
        </div>
        <div style={{ 
          fontSize: '16px', fontWeight: 'bold', marginTop: '6px',
          backgroundColor: '#fef3c7', padding: '4px 8px',
          borderRadius: '4px', display: 'inline-block',
        }}>
          PIN: {order.delivery_address?.pincode || 'N/A'}
        </div>
        <div style={{ fontSize: '11px', marginTop: '8px', paddingTop: '6px', borderTop: '1px solid #e5e7eb' }}>
          <strong>Phone:</strong> {order.delivery_address?.phone || order.phone || 'N/A'}
        </div>
      </div>

      {/* Order Items Table */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{ 
          fontSize: '9px', color: '#666', textTransform: 'uppercase', 
          letterSpacing: '1px', marginBottom: '6px', fontWeight: '600',
        }}>
          Items ({order.items?.length || 0}):
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6' }}>
              <th style={{ padding: '6px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Product</th>
              <th style={{ padding: '6px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', width: '35px' }}>Qty</th>
              <th style={{ padding: '6px', textAlign: 'right', borderBottom: '2px solid #e5e7eb', width: '55px' }}>Price</th>
              <th style={{ padding: '6px', textAlign: 'right', borderBottom: '2px solid #e5e7eb', width: '60px' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {order.items?.map((item, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '6px' }}>
                  <div style={{ fontWeight: '500' }}>{item.title}</div>
                </td>
                <td style={{ padding: '6px', textAlign: 'center', fontWeight: '600' }}>
                  {item.quantity}
                </td>
                <td style={{ padding: '6px', textAlign: 'right' }}>
                  ₹{item.price}
                </td>
                <td style={{ padding: '6px', textAlign: 'right', fontWeight: '600' }}>
                  ₹{(item.price * item.quantity).toFixed(0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Total Section */}
      <div style={{ 
        backgroundColor: '#1f2937', color: '#ffffff',
        padding: '10px 12px', borderRadius: '6px', marginBottom: '10px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '10px', opacity: '0.8' }}>TOTAL AMOUNT</div>
            {order.payment_method === 'cod' && (
              <div style={{ fontSize: '10px', color: '#fbbf24', marginTop: '2px' }}>
                Collect ₹{order.total_amount?.toFixed(0)} on delivery
              </div>
            )}
          </div>
          <div style={{ fontSize: '22px', fontWeight: 'bold' }}>
            ₹{order.total_amount?.toFixed(0) || '0'}
          </div>
        </div>
      </div>

      {/* Courier / Tracking Info */}
      {(order.tracking_number || order.shadowfax_awb) && !hasShadowfax && (
        <div style={{ 
          backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
          padding: '8px 10px', borderRadius: '6px', marginBottom: '10px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase' }}>Tracking Number</div>
              <div style={{ fontSize: '12px', fontWeight: 'bold', fontFamily: 'monospace', marginTop: '2px' }}>
                {order.tracking_number}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase' }}>Courier</div>
              <div style={{ fontSize: '11px', fontWeight: '500', marginTop: '2px' }}>
                {order.courier_name || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ 
        borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#666' }}>
          <div>Thank you for shopping with us!</div>
          <div style={{ fontWeight: '500' }}>tiffinandbag.com</div>
        </div>
        <div style={{ 
          textAlign: 'center', fontSize: '8px', color: '#9ca3af',
          marginTop: '6px', padding: '4px', backgroundColor: '#f9fafb', borderRadius: '4px',
        }}>
          For queries: WhatsApp: +91 88103 45965
        </div>
      </div>

      {/* Cut Line */}
      <div style={{ 
        marginTop: '10px', borderTop: '2px dashed #d1d5db',
        paddingTop: '6px', textAlign: 'center', fontSize: '9px', color: '#9ca3af',
      }}>
        - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      </div>
    </div>
  );
});

DispatchSlip.displayName = 'DispatchSlip';

// Bulk Print Component - 2 slips per A4 page
const BulkDispatchSlips = React.forwardRef(({ orders }, ref) => {
  if (!orders || orders.length === 0) return null;
  
  // Pair orders for 2 per page
  const pairs = [];
  for (let i = 0; i < orders.length; i += 2) {
    pairs.push(orders.slice(i, i + 2));
  }
  
  return (
    <div ref={ref}>
      {pairs.map((pair, pageIdx) => (
        <div 
          key={pageIdx} 
          style={{ 
            pageBreakAfter: pageIdx < pairs.length - 1 ? 'always' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10mm',
            padding: '5mm',
          }}
        >
          {pair.map((order, idx) => (
            <DispatchSlip key={order.id || idx} order={order} />
          ))}
        </div>
      ))}
    </div>
  );
});

BulkDispatchSlips.displayName = 'BulkDispatchSlips';

// Risk level colors
const getRiskBadge = (level) => {
  switch (level) {
    case 'high':
      return <Badge className="bg-red-100 text-red-800"><AlertTriangle className="w-3 h-3 mr-1" />High Risk</Badge>;
    case 'medium':
      return <Badge className="bg-amber-100 text-amber-800"><Info className="w-3 h-3 mr-1" />Medium Risk</Badge>;
    case 'low':
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Low Risk</Badge>;
    default:
      return <Badge className="bg-gray-100 text-gray-800">Not Assessed</Badge>;
  }
};

function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courierName, setCourierName] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDispatchSlip, setShowDispatchSlip] = useState(false);
  const printRef = useRef();
  
  // Bulk print states
  const [showBulkPrintModal, setShowBulkPrintModal] = useState(false);
  const [bulkPrintStartDate, setBulkPrintStartDate] = useState('');
  const [bulkPrintEndDate, setBulkPrintEndDate] = useState('');
  const [selectedOrdersForPrint, setSelectedOrdersForPrint] = useState([]);
  const [bulkPrintOrders, setBulkPrintOrders] = useState([]);
  const bulkPrintRef = useRef();
  
  // AI Risk Assessment states
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [assessingRisk, setAssessingRisk] = useState(false);
  const [riskAssessment, setRiskAssessment] = useState(null);

  // Shadowfax states
  const [sfxChecking, setSfxChecking] = useState(false);
  const [sfxServiceability, setSfxServiceability] = useState(null);
  const [sfxCreating, setSfxCreating] = useState(false);
  const [sfxResult, setSfxResult] = useState(null);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await api.adminGetOrders(token);
      setOrders(data.orders || []);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  // Filter orders for bulk print by date range
  const filterOrdersForBulkPrint = () => {
    if (!bulkPrintStartDate || !bulkPrintEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    const start = new Date(bulkPrintStartDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(bulkPrintEndDate);
    end.setHours(23, 59, 59, 999);
    
    const filtered = orders.filter(order => {
      const orderDate = new Date(order.created_at);
      return orderDate >= start && orderDate <= end && 
             (order.order_status === 'confirmed' || order.order_status === 'shipped');
    });
    
    setBulkPrintOrders(filtered);
    setSelectedOrdersForPrint(filtered.map(o => o.id));
  };

  // Handle bulk print
  const handleBulkPrint = () => {
    const ordersToPrint = bulkPrintOrders.filter(o => selectedOrdersForPrint.includes(o.id));
    if (ordersToPrint.length === 0) {
      toast.error('No orders selected for printing');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Dispatch Slips - Tiffin and Bag</title>
          <style>
            @page { size: A4; margin: 5mm; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
            .slip-container { 
              display: flex; 
              flex-direction: column; 
              gap: 5mm;
              padding: 5mm;
            }
            .slip {
              border: 2px solid #1f2937;
              border-radius: 8px;
              padding: 15px;
              page-break-inside: avoid;
              min-height: 140mm;
            }
            .page-break { page-break-after: always; }
            .header { display: flex; justify-content: space-between; border-bottom: 2px solid #1f2937; padding-bottom: 10px; margin-bottom: 10px; }
            .order-id { font-size: 18px; font-weight: bold; }
            .payment-badge { padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; }
            .paid { background: #dcfce7; color: #16a34a; }
            .cod { background: #fff7ed; color: #ea580c; }
            .section { margin-bottom: 10px; }
            .section-title { font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
            .address { font-size: 13px; line-height: 1.5; }
            .items-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 10px; }
            .items-table th, .items-table td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            .items-table th { background: #f3f4f6; }
            .total-row { font-weight: bold; font-size: 16px; }
            .footer { margin-top: 10px; padding-top: 10px; border-top: 2px solid #1f2937; display: flex; justify-content: space-between; }
            .cut-line { text-align: center; color: #999; font-size: 10px; margin: 5mm 0; }
            .sfx-bar { border: 2px solid #4338ca; border-radius: 8px; padding: 10px; margin-bottom: 10px; background: #eef2ff; text-align: center; }
            .sfx-bar .sfx-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
            .sfx-bar .sfx-label { font-size: 12px; font-weight: bold; color: #4338ca; }
            .sfx-bar .sfx-awb { font-size: 11px; color: #4338ca; font-weight: 600; margin-top: 4px; }
            .sfx-badge-prepaid { font-size: 11px; font-weight: bold; color: #fff; background: #16a34a; padding: 2px 10px; border-radius: 12px; }
            .sfx-badge-cod { font-size: 11px; font-weight: bold; color: #fff; background: #ea580c; padding: 2px 10px; border-radius: 12px; }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.12.3/dist/JsBarcode.all.min.js"><\/script>
        </head>
        <body>
    `);
    
    for (let i = 0; i < ordersToPrint.length; i += 2) {
      const pair = ordersToPrint.slice(i, i + 2);
      printWindow.document.write('<div class="slip-container">');
      
      pair.forEach((order, idx) => {
        const address = order.delivery_address || {};
        const paymentInfo = order.payment_method === 'cod' ? { class: 'cod', text: 'COD' } : { class: 'paid', text: 'PREPAID' };
        const orderId = order.id?.slice(0, 8).toUpperCase() || 'N/A';
        
        printWindow.document.write(`
          <div class="slip">
            ${order.shadowfax_awb ? `
              <div class="sfx-bar">
                <div class="sfx-header">
                  <span class="sfx-label">SHADOWFAX</span>
                  <span class="${order.payment_method === 'cod' ? 'sfx-badge-cod' : 'sfx-badge-prepaid'}">
                    ${order.payment_method === 'cod' ? 'COD ₹' + order.total_amount : 'PREPAID'}
                  </span>
                </div>
                <svg id="barcode-${orderId}"></svg>
                <div class="sfx-awb">AWB: ${order.shadowfax_awb}</div>
              </div>
            ` : ''}
            <div class="header">
              <div>
                <div class="order-id">#${orderId}</div>
                <div style="font-size: 12px; color: #666;">Tiffin and Bag</div>
              </div>
              <div style="text-align: right;">
                <span class="payment-badge ${paymentInfo.class}">${paymentInfo.text}</span>
                <div style="font-size: 11px; color: #666; margin-top: 5px;">
                  ${new Date(order.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
            </div>
            
            <div class="section">
              <div class="section-title">Ship To</div>
              <div class="address">
                <strong>${address.name || 'N/A'}</strong><br>
                ${address.line1 || ''}<br>
                ${address.line2 ? address.line2 + '<br>' : ''}
                ${address.landmark ? 'Landmark: ' + address.landmark + '<br>' : ''}
                ${address.city || ''}, ${address.state || ''} - ${address.pincode || ''}<br>
                📱 ${address.phone || order.phone || 'N/A'}
              </div>
            </div>
            
            <table class="items-table">
              <thead>
                <tr><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Price</th></tr>
              </thead>
              <tbody>
                ${(order.items || []).map(item => `
                  <tr>
                    <td>${item.title || item.product_id}</td>
                    <td style="text-align:center">${item.quantity}</td>
                    <td style="text-align:right">₹${item.price}</td>
                  </tr>
                `).join('')}
                <tr class="total-row">
                  <td colspan="2">Total</td>
                  <td style="text-align:right">₹${order.total_amount}</td>
                </tr>
              </tbody>
            </table>
            
            <div class="footer">
              <div style="font-size: 11px;">
                ${order.payment_method === 'cod' ? '💵 Collect ₹' + order.total_amount + ' on delivery' : '✓ Prepaid - Do not collect'}
              </div>
              <div style="font-size: 10px; color: #666;">
                WhatsApp: +91 80621 80632
              </div>
            </div>
          </div>
          ${idx === 0 && pair.length > 1 ? '<div class="cut-line">✂️ - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -</div>' : ''}
        `);
      });
      
      printWindow.document.write('</div>');
      if (i + 2 < ordersToPrint.length) {
        printWindow.document.write('<div class="page-break"></div>');
      }
    }
    
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    
    // Generate barcodes for Shadowfax orders after DOM is ready
    printWindow.onload = () => {
      ordersToPrint.forEach(order => {
        if (order.shadowfax_awb) {
          const orderId = order.id?.slice(0, 8).toUpperCase() || 'N/A';
          try {
            printWindow.JsBarcode(`#barcode-${orderId}`, order.shadowfax_awb, {
              format: 'CODE128', width: 1.5, height: 40, displayValue: true,
              fontSize: 11, font: 'monospace', margin: 4,
            });
          } catch (e) { /* skip barcode if fails */ }
        }
      });
      setTimeout(() => printWindow.print(), 300);
    };
    
    toast.success(`Printing ${ordersToPrint.length} dispatch slips`);
    setShowBulkPrintModal(false);
  };

  // AI Risk Assessment
  const assessOrderRisk = async (order) => {
    setAssessingRisk(true);
    setRiskAssessment(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${order.id}/assess-risk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const result = await response.json();
      if (result.error) {
        toast.error(result.error);
      } else {
        setRiskAssessment(result);
      }
    } catch (error) {
      toast.error('Failed to assess risk');
    } finally {
      setAssessingRisk(false);
    }
  };

  // Shadowfax: Check Serviceability
  const checkSfxServiceability = async (order) => {
    setSfxChecking(true);
    setSfxServiceability(null);
    setSfxResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/shadowfax/check-serviceability/${order.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      setSfxServiceability(result);
      if (result.serviceable) {
        toast.success(`Pincode ${result.pincode} is serviceable by Shadowfax!`);
      } else {
        toast.error(result.reason || 'Not serviceable by Shadowfax');
      }
    } catch (error) {
      toast.error('Serviceability check failed');
    } finally {
      setSfxChecking(false);
    }
  };

  // Shadowfax: Create Order
  const createSfxOrder = async (order) => {
    setSfxCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/shadowfax/create-order/${order.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      setSfxResult(result);
      if (result.success) {
        toast.success(`Shadowfax order created! ID: ${result.sfx_order_id}`);
        fetchOrders();
      } else {
        toast.error(result.error || 'Failed to create Shadowfax order');
      }
    } catch (error) {
      toast.error('Failed to create Shadowfax order');
    } finally {
      setSfxCreating(false);
    }
  };

  // Shadowfax: Reset order data
  const resetSfxOrder = async (order) => {
    if (!window.confirm('Clear Shadowfax data for this order? You can then re-create it.')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/shadowfax/reset-order/${order.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      toast.success(result.message || 'Shadowfax data cleared');
      setSfxResult(null);
      setSfxServiceability(null);
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  // Reset Shadowfax state when order changes
  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setSfxServiceability(null);
    setSfxResult(null);
    // Pre-populate if order already has SFX data
    if (order.shadowfax_awb) {
      setSfxResult({
        success: true,
        awb_number: order.shadowfax_awb,
        sfx_order_id: order.shadowfax_order_id,
        status: order.shadowfax_status,
      });
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdating(true);
    try {
      await api.adminUpdateOrderStatus(orderId, newStatus, token);
      toast.success('Order status updated!');
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddTracking = async () => {
    if (!trackingNumber || !courierName) {
      toast.error('Please enter tracking number and courier name');
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${selectedOrder.id}/tracking`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tracking_number: trackingNumber,
          courier_name: courierName,
        }),
      });

      if (response.ok) {
        toast.success('Tracking information added!');
        fetchOrders();
        setTrackingNumber('');
        setCourierName('');
      } else {
        toast.error('Failed to add tracking');
      }
    } catch (error) {
      toast.error('Error adding tracking');
    } finally {
      setUpdating(false);
    }
  };

  const handlePrintDispatchSlip = () => {
    setShowDispatchSlip(true);
  };

  const handlePrint = () => {
    const printContent = document.getElementById('dispatch-slip-content');
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Dispatch Slip - Order #${selectedOrder?.id?.slice(0, 8).toUpperCase()}</title>
        <style>
          @page {
            size: A5;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, Helvetica, sans-serif;
          }
          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        ${printContent.outerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Package className="w-5 h-5" />;
      case 'confirmed':
        return <CheckCircle2 className="w-5 h-5" />;
      case 'shipped':
        return <Truck className="w-5 h-5" />;
      case 'delivered':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      default:
        return <XCircle className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = filter === 'all' || order.order_status === filter;
    if (!matchesStatus) return false;
    if (!searchQuery.trim()) return true;
    
    const q = searchQuery.toLowerCase().trim();
    const name = (order.delivery_address?.name || '').toLowerCase();
    const phone = (order.delivery_address?.phone || order.phone || '').toLowerCase();
    const city = (order.delivery_address?.city || '').toLowerCase();
    const pincode = (order.delivery_address?.pincode || '').toString();
    const orderId = (order.id || '').toLowerCase();
    const awb = (order.shadowfax_awb || order.tracking_number || '').toLowerCase();
    const razorpayId = (order.razorpay_payment_id || '').toLowerCase();
    
    return name.includes(q) || phone.includes(q) || city.includes(q) || 
           pincode.includes(q) || orderId.includes(q) || awb.includes(q) || razorpayId.includes(q);
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold">Order Management</h1>
            <p className="text-muted-foreground">Manage and track all orders</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowBulkPrintModal(true)} 
              variant="outline"
              className="gap-2"
              data-testid="bulk-print-btn"
            >
              <FileText className="w-4 h-4" />
              Bulk Print Slips
            </Button>
            <Button onClick={fetchOrders} variant="outline" data-testid="refresh-orders-btn">
              Refresh
            </Button>
          </div>
        </div>

        {/* Filter Tabs */}
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all">All Orders</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="confirmed">Confirmed</TabsTrigger>
            <TabsTrigger value="shipped">Shipped</TabsTrigger>
            <TabsTrigger value="delivered">Delivered</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, order ID, city, pincode, AWB..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="order-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-sm text-muted-foreground">
            {filteredOrders.length} result{filteredOrders.length !== 1 ? 's' : ''} for "{searchQuery}"
          </p>
        )}

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">No orders found</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredOrders.map((order) => {
              const paymentInfo = getPaymentInfo(order);
              return (
              <Card
                key={order.id}
                className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => openOrderDetails(order)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      {getStatusIcon(order.order_status)}
                      <h3 className="font-semibold">Order #{order.id.slice(0, 8)}</h3>
                      {/* Payment Status Badge */}
                      <Badge className={`${paymentInfo.color} border flex items-center gap-1`}>
                        {paymentInfo.icon}
                        {paymentInfo.label}
                      </Badge>
                      {/* Order Status Badge */}
                      <Badge className={getStatusColor(order.order_status)}>
                        {order.order_status}
                      </Badge>
                    </div>
                    
                    {/* Order Items Preview with Images */}
                    <div className="flex gap-2 mt-3 mb-3 overflow-x-auto pb-2">
                      {order.items?.slice(0, 4).map((item, idx) => (
                        <div key={idx} className="flex-shrink-0 flex items-center gap-2 bg-gray-50 rounded-lg p-2 pr-3">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt={item.title}
                              className="w-10 h-10 object-cover rounded"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                              <Package className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate max-w-[120px]">{item.title}</p>
                            <p className="text-xs text-gray-500">×{item.quantity}</p>
                          </div>
                        </div>
                      ))}
                      {order.items?.length > 4 && (
                        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-xs font-medium text-gray-500">
                          +{order.items.length - 4}
                        </div>
                      )}
                    </div>

                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Customer</p>
                        <p className="font-medium">{order.delivery_address?.name || order.phone}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Items</p>
                        <p className="font-medium">{order.items?.length} items</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Amount</p>
                        <p className="font-medium text-lg">₹{order.total_amount.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    {new Date(order.created_at).toLocaleDateString('en-IN', {
                      timeZone: 'Asia/Kolkata',
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </div>
                </div>
              </Card>
            );
            })}
          </div>
        )}

        {/* Order Details Modal */}
        {selectedOrder && (
          <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <DialogTitle>Order Details - #{selectedOrder.id.slice(0, 8)}</DialogTitle>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => { setShowRiskModal(true); assessOrderRisk(selectedOrder); }}
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      data-testid="assess-risk-btn"
                    >
                      <ShieldAlert className="w-4 h-4" />
                      AI Risk Check
                    </Button>
                    <Button 
                      onClick={handlePrintDispatchSlip}
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      data-testid="print-dispatch-slip-btn"
                    >
                      <Printer className="w-4 h-4" />
                      Print Slip
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6">
                {/* Payment Status */}
                {(() => {
                  const paymentInfo = getPaymentInfo(selectedOrder);
                  return (
                    <Card className="p-4">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        Payment Status
                        <Badge className={`${paymentInfo.color} border flex items-center gap-1`}>
                          {paymentInfo.icon}
                          {paymentInfo.label}
                        </Badge>
                      </h3>
                      <div className="text-sm space-y-1">
                        <p><strong>Method:</strong> {selectedOrder.payment_method === 'cod' ? 'Cash on Delivery' : 'Online (Razorpay)'}</p>
                        <p><strong>Status:</strong> {paymentInfo.slipText}</p>
                        {selectedOrder.razorpay_payment_id && (
                          <p><strong>Razorpay Payment ID:</strong> <code className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono">{selectedOrder.razorpay_payment_id}</code></p>
                        )}
                        {selectedOrder.razorpay_order_id && (
                          <p><strong>Razorpay Order ID:</strong> <code className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono">{selectedOrder.razorpay_order_id}</code></p>
                        )}
                        {selectedOrder.payment_method !== 'cod' && selectedOrder.payment_status === 'created' && !selectedOrder.razorpay_payment_id && (
                          <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Payment initiated but not completed. Customer may have abandoned checkout.
                          </p>
                        )}
                      </div>
                    </Card>
                  );
                })()}
                
                {/* Order Status */}
                <div>
                  <Label>Order Status</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      variant={selectedOrder.order_status === 'confirmed' ? 'default' : 'outline'}
                      onClick={() => handleStatusUpdate(selectedOrder.id, 'confirmed')}
                      disabled={updating}
                    >
                      Confirmed
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedOrder.order_status === 'shipped' ? 'default' : 'outline'}
                      onClick={() => handleStatusUpdate(selectedOrder.id, 'shipped')}
                      disabled={updating}
                    >
                      Shipped
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedOrder.order_status === 'delivered' ? 'default' : 'outline'}
                      onClick={() => handleStatusUpdate(selectedOrder.id, 'delivered')}
                      disabled={updating}
                    >
                      Delivered
                    </Button>
                  </div>
                </div>

                {/* Shipping Details */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Shipping Address</h3>
                  <div className="space-y-1 text-sm">
                    <p><strong>Name:</strong> {selectedOrder.delivery_address?.name}</p>
                    <p><strong>Phone:</strong> {selectedOrder.delivery_address?.phone || selectedOrder.phone}</p>
                    <p><strong>Flat/House:</strong> {selectedOrder.delivery_address?.line1}</p>
                    {selectedOrder.delivery_address?.line2 && (
                      <p><strong>Area/Street:</strong> {selectedOrder.delivery_address?.line2}</p>
                    )}
                    {selectedOrder.delivery_address?.landmark && (
                      <p><strong>Landmark:</strong> {selectedOrder.delivery_address?.landmark}</p>
                    )}
                    <p><strong>City:</strong> {selectedOrder.delivery_address?.city}, <strong>State:</strong> {selectedOrder.delivery_address?.state}</p>
                    <p><strong>Pincode:</strong> {selectedOrder.delivery_address?.pincode}</p>
                    {selectedOrder.delivery_address?.email && (
                      <p><strong>Email:</strong> {selectedOrder.delivery_address?.email}</p>
                    )}
                  </div>
                  {/* Full address copy button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-xs gap-1"
                    data-testid="copy-address-btn"
                    onClick={() => {
                      const a = selectedOrder.delivery_address || {};
                      const full = [a.name, a.line1, a.line2, a.landmark, a.city, a.state, a.pincode, a.phone].filter(Boolean).join(', ');
                      navigator.clipboard.writeText(full);
                      toast.success('Address copied!');
                    }}
                  >
                    <Download className="w-3 h-3" /> Copy Full Address
                  </Button>
                </Card>

                {/* Add Tracking */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Tracking Information</h3>
                  {selectedOrder.tracking_number ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm"><strong>Tracking Number:</strong> {selectedOrder.tracking_number}</p>
                      <p className="text-sm"><strong>Courier:</strong> {selectedOrder.courier_name}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <Label>Courier Name</Label>
                        <Input
                          value={courierName}
                          onChange={(e) => setCourierName(e.target.value)}
                          placeholder="Blue Dart, Delhivery, etc."
                        />
                      </div>
                      <div>
                        <Label>Tracking Number</Label>
                        <Input
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                          placeholder="Enter tracking number"
                        />
                      </div>
                      <Button onClick={handleAddTracking} disabled={updating}>
                        {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Tracking'}
                      </Button>
                    </div>
                  )}
                </Card>

                {/* Shadowfax Courier Integration */}
                <Card className="p-4 border-2 border-indigo-100" data-testid="shadowfax-section">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-indigo-600" />
                    Shadowfax Courier
                  </h3>

                  {/* Already has SFX order */}
                  {(selectedOrder.shadowfax_awb || sfxResult?.success) ? (
                    <div className="space-y-3">
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <Badge className="bg-indigo-100 text-indigo-800">
                            <Truck className="w-3 h-3 mr-1" />
                            {sfxResult?.status || selectedOrder.shadowfax_status || 'new'}
                          </Badge>
                          <span className="text-xs text-muted-foreground font-mono">
                            AWB: {sfxResult?.awb_number || selectedOrder.shadowfax_awb}
                          </span>
                        </div>
                        {selectedOrder.shadowfax_rider && (
                          <p className="text-xs text-muted-foreground">
                            Rider: {selectedOrder.shadowfax_rider.name} ({selectedOrder.shadowfax_rider.contact})
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-red-500 hover:text-red-700"
                        onClick={() => resetSfxOrder(selectedOrder)}
                        data-testid="sfx-reset-btn"
                      >
                        <XCircle className="w-3 h-3 mr-1" /> Reset Shadowfax Data
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Step 1: Check Serviceability */}
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          Delivery Pincode: <strong>{selectedOrder.delivery_address?.pincode || 'N/A'}</strong>
                        </p>
                        <Button
                          onClick={() => checkSfxServiceability(selectedOrder)}
                          disabled={sfxChecking || !selectedOrder.delivery_address?.pincode}
                          variant="outline"
                          size="sm"
                          className="w-full gap-2"
                          data-testid="sfx-check-serviceability-btn"
                        >
                          {sfxChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                          Check Shadowfax Serviceability
                        </Button>
                      </div>

                      {/* Serviceability Result */}
                      {sfxServiceability && (
                        <div className={`rounded-lg p-3 text-sm ${
                          sfxServiceability.serviceable
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-red-50 border border-red-200'
                        }`}>
                          {sfxServiceability.serviceable ? (
                            <div className="space-y-1">
                              <p className="font-medium text-green-800 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" /> Serviceable!
                              </p>
                              {sfxServiceability.services && (
                                <p>Services: <strong>{sfxServiceability.services.join(', ')}</strong></p>
                              )}
                            </div>
                          ) : (
                            <p className="text-red-700 flex items-center gap-1">
                              <XOctagon className="w-4 h-4" />
                              {sfxServiceability.reason || 'Not serviceable at this pincode'}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Step 2: Create Shadowfax Order (only if serviceable) */}
                      {sfxServiceability?.serviceable && (
                        <Button
                          onClick={() => createSfxOrder(selectedOrder)}
                          disabled={sfxCreating}
                          className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700"
                          data-testid="sfx-create-order-btn"
                        >
                          {sfxCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                          Create Shadowfax Order
                        </Button>
                      )}

                      {/* Creation Result (error case) */}
                      {sfxResult && !sfxResult.success && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                          <p className="font-medium">Order creation failed</p>
                          <p className="text-xs mt-1">{sfxResult.error}</p>
                        </div>
                      )}
                    </div>
                  )}
                </Card>

                {/* Order Items with Images */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">Order Items</h3>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, index) => (
                      <div key={index} className="flex items-center gap-4 py-3 border-b last:border-0">
                        {/* Product Image */}
                        {item.image ? (
                          <img 
                            src={item.image} 
                            alt={item.title}
                            className="w-16 h-16 object-cover rounded-lg border"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">
                            Qty: {item.quantity} × ₹{item.price}
                          </p>
                          {item.product_id && (
                            <p className="text-xs text-gray-400">ID: {item.product_id}</p>
                          )}
                        </div>
                        {/* Price */}
                        <p className="font-semibold text-lg">₹{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total</span>
                      <span className="text-xl font-bold">₹{selectedOrder.total_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </Card>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Dispatch Slip Modal for Printing */}
        {showDispatchSlip && selectedOrder && (
          <Dialog open={showDispatchSlip} onOpenChange={setShowDispatchSlip}>
            <DialogContent className="max-w-[180mm] p-0 overflow-visible">
              <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                <div>
                  <h3 className="font-semibold">Dispatch Slip</h3>
                  <p className="text-xs text-gray-500">Order #{selectedOrder.id?.slice(0, 8).toUpperCase()}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handlePrint} size="sm" className="bg-blue-600 hover:bg-blue-700">
                    <Printer className="w-4 h-4 mr-2" />
                    Print A5
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowDispatchSlip(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="overflow-auto max-h-[75vh] bg-gray-100 p-4 flex justify-center">
                <div className="shadow-xl">
                  <DispatchSlip ref={printRef} order={selectedOrder} />
                </div>
              </div>
              <div className="p-3 border-t bg-gray-50 text-center text-xs text-gray-500">
                Click "Print A5" to open the print dialog with A5 paper size preset
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Bulk Print Modal */}
        <Dialog open={showBulkPrintModal} onOpenChange={setShowBulkPrintModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Bulk Print Dispatch Slips
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Select a date range to filter orders, then print 2 dispatch slips per A4 page.</p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bulk-start-date">Start Date</Label>
                  <Input
                    id="bulk-start-date"
                    type="date"
                    value={bulkPrintStartDate}
                    onChange={(e) => setBulkPrintStartDate(e.target.value)}
                    data-testid="bulk-print-start-date"
                  />
                </div>
                <div>
                  <Label htmlFor="bulk-end-date">End Date</Label>
                  <Input
                    id="bulk-end-date"
                    type="date"
                    value={bulkPrintEndDate}
                    onChange={(e) => setBulkPrintEndDate(e.target.value)}
                    data-testid="bulk-print-end-date"
                  />
                </div>
              </div>

              <Button onClick={filterOrdersForBulkPrint} className="w-full gap-2" data-testid="filter-orders-btn">
                <Calendar className="w-4 h-4" />
                Filter Orders
              </Button>

              {bulkPrintOrders.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{bulkPrintOrders.length} orders found (Confirmed/Shipped only)</p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedOrdersForPrint(bulkPrintOrders.map(o => o.id))}
                        data-testid="select-all-orders-btn"
                      >
                        Select All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedOrdersForPrint([])}
                        data-testid="deselect-all-orders-btn"
                      >
                        Deselect All
                      </Button>
                    </div>
                  </div>

                  <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
                    {bulkPrintOrders.map((order) => {
                      const paymentInfo = getPaymentInfo(order);
                      return (
                        <div
                          key={order.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50"
                          data-testid={`bulk-order-row-${order.id.slice(0, 8)}`}
                        >
                          <Checkbox
                            checked={selectedOrdersForPrint.includes(order.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedOrdersForPrint(prev => [...prev, order.id]);
                              } else {
                                setSelectedOrdersForPrint(prev => prev.filter(id => id !== order.id));
                              }
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-sm font-medium">#{order.id.slice(0, 8)}</span>
                              <Badge className={`${paymentInfo.color} border text-xs`}>
                                {paymentInfo.label}
                              </Badge>
                              <Badge className={`${getStatusColor(order.order_status)} text-xs`}>
                                {order.order_status}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 truncate">
                              {order.delivery_address?.name} - {order.delivery_address?.city}, {order.delivery_address?.pincode}
                            </p>
                          </div>
                          <span className="font-semibold text-sm whitespace-nowrap">₹{order.total_amount}</span>
                        </div>
                      );
                    })}
                  </div>

                  <Button
                    onClick={handleBulkPrint}
                    className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                    disabled={selectedOrdersForPrint.length === 0}
                    data-testid="bulk-print-execute-btn"
                  >
                    <Printer className="w-4 h-4" />
                    Print {selectedOrdersForPrint.length} Dispatch Slips (2 per A4)
                  </Button>
                </div>
              )}

              {bulkPrintOrders.length === 0 && bulkPrintStartDate && bulkPrintEndDate && (
                <div className="text-center py-6 text-muted-foreground" data-testid="no-orders-found">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No confirmed/shipped orders found in this date range.</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* AI Risk Assessment Modal */}
        <Dialog open={showRiskModal} onOpenChange={setShowRiskModal}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5" />
                AI Order Risk Assessment
              </DialogTitle>
            </DialogHeader>

            {assessingRisk ? (
              <div className="flex flex-col items-center py-10 gap-3" data-testid="risk-loading">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <p className="text-sm text-muted-foreground">Analyzing order with AI...</p>
                <p className="text-xs text-muted-foreground">Checking address, payment, customer history</p>
              </div>
            ) : riskAssessment?.error ? (
              <div className="text-center py-8" data-testid="risk-error">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-red-600">{riskAssessment.error}</p>
              </div>
            ) : riskAssessment ? (
              <div className="space-y-4" data-testid="risk-results">
                {/* Risk Level Header */}
                <div className={`rounded-lg p-4 text-center ${
                  riskAssessment.risk_level === 'high' ? 'bg-red-50 border border-red-200' :
                  riskAssessment.risk_level === 'medium' ? 'bg-amber-50 border border-amber-200' :
                  'bg-green-50 border border-green-200'
                }`}>
                  <div className="text-2xl font-bold mb-1">
                    {getRiskBadge(riskAssessment.risk_level)}
                  </div>
                  <p className="text-sm mt-2">{riskAssessment.summary}</p>
                  <div className="mt-2 text-xs text-muted-foreground">
                    Risk Score: <span className="font-bold text-base">{riskAssessment.risk_score}/10</span>
                  </div>
                </div>

                {/* Recommendation */}
                <Card className="p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-blue-500" />
                    Recommendation
                  </h4>
                  <Badge className={`text-sm ${
                    riskAssessment.recommendation === 'ship' ? 'bg-green-100 text-green-800' :
                    riskAssessment.recommendation === 'verify_first' ? 'bg-amber-100 text-amber-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {riskAssessment.recommendation === 'ship' ? 'Safe to Ship' :
                     riskAssessment.recommendation === 'verify_first' ? 'Verify Before Shipping' :
                     'Consider Rejecting'}
                  </Badge>
                  {riskAssessment.verification_steps && riskAssessment.verification_steps.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {riskAssessment.verification_steps.map((step, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                          <span className="font-bold text-amber-600">{i + 1}.</span>
                          {step}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>

                {/* Risk Factors */}
                {riskAssessment.factors && riskAssessment.factors.length > 0 && (
                  <Card className="p-4">
                    <h4 className="font-semibold text-sm mb-3">Risk Factors</h4>
                    <div className="space-y-2">
                      {riskAssessment.factors.map((factor, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className={`mt-0.5 flex-shrink-0 ${
                            factor.impact === 'positive' ? 'text-green-500' :
                            factor.impact === 'negative' ? 'text-red-500' :
                            'text-gray-400'
                          }`}>
                            {factor.impact === 'positive' ? <CheckCircle className="w-4 h-4" /> :
                             factor.impact === 'negative' ? <AlertTriangle className="w-4 h-4" /> :
                             <Info className="w-4 h-4" />}
                          </span>
                          <div>
                            <span className="font-medium">{factor.factor}</span>
                            <p className="text-xs text-muted-foreground">{factor.detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Customer Stats */}
                {riskAssessment.customer_stats && (
                  <Card className="p-4">
                    <h4 className="font-semibold text-sm mb-2">Customer History</h4>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <p className="text-lg font-bold">{riskAssessment.customer_stats.total_orders}</p>
                        <p className="text-xs text-muted-foreground">Total Orders</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-green-600">{riskAssessment.customer_stats.delivered}</p>
                        <p className="text-xs text-muted-foreground">Delivered</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-red-600">{riskAssessment.customer_stats.cancelled}</p>
                        <p className="text-xs text-muted-foreground">Cancelled</p>
                      </div>
                    </div>
                  </Card>
                )}

                <p className="text-xs text-muted-foreground text-center">
                  Assessed at {riskAssessment.assessed_at ? formatDateTimeIST(riskAssessment.assessed_at) : 'just now'}
                </p>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default AdminOrdersPage;
